/* src/ui/panels/miniGame.ts — the things you actually do at the cart.
 *
 * Prep and wiping down used to be one-click buttons, which made them accounting rather than
 * work. Each is now a short skill interaction that returns a **quality** in [0,1].
 *
 * ── Cooking is one mechanic, repeated ─────────────────────────────────────────────────
 * Stop the sweep in the hot zone. Two beats to a serving — grill the patty, build the burger;
 * pour the batter, flip and fold — so cooking ten portions is ten portions of work rather than
 * one roll of the dice for the whole batch. The words change per cuisine, the mechanic does
 * not: an earlier version gave each cuisine its own primitive (tap targets, a drag path, a
 * hold-and-release) and the variety got in the way of the rhythm.
 *
 * A session is capped, and stops early whenever the player says so — they keep whatever they
 * have finished. Half-cooked servings do not count.
 *
 * ── The security shape, because this touches the economy ──────────────────────────────
 * Quality is computed on the client, so it is not trustworthy — and it does not have to be.
 * The server treats it as **penalty-only**: 1.0 is exactly what the old one-click version gave,
 * and anything less gives less (0016). A client that lies and always sends 1.0 gets what a
 * player with good timing gets and no more. No sequence of inputs here can create money, which
 * is the only invariant that matters (CLAUDE.md non-negotiable #2).
 *
 * Every game resolves to `null` if cancelled — the panel closed, Escape pressed — and leaves
 * nothing behind: no timers, no listeners, no half-torn DOM.
 */

import { el } from '../dom.ts';

export interface Game {
  readonly el: HTMLElement;
  readonly done: Promise<number | null>;
  cancel: () => void;
}

/* ── Shared plumbing ──────────────────────────────────────────────────────────────────── */

/**
 * A promise plus the handle to settle it once. Several paths race to be the one that settles —
 * a hit, the timer, the panel closing — so "settle at most once" is the whole contract.
 */
function deferred<T>(): {
  readonly promise: Promise<T | null>;
  readonly settle: (value: T | null) => void;
  readonly settled: boolean;
} {
  let resolveFn: (value: T | null) => void = () => undefined;
  let done = false;
  const promise = new Promise<T | null>((resolve) => {
    resolveFn = resolve;
  });
  return {
    promise,
    settle: (value): void => {
      if (done) return;
      done = true;
      resolveFn(value);
    },
    get settled(): boolean {
      return done;
    },
  };
}

/** Countdown bar, for the games that are against the clock. */
function timerBar(seconds: number, onExpire: () => void): { el: HTMLElement; stop: () => void } {
  const fill = el('div', { class: 'gf-mg-timerfill' });
  const node = el('div', { class: 'gf-mg-timer' }, fill);
  let raf = 0;
  let start = 0;
  const tick = (now: number): void => {
    if (start === 0) start = now;
    const left = Math.max(0, 1 - (now - start) / (seconds * 1000));
    fill.style.transform = `scaleX(${String(left)})`;
    if (left <= 0) {
      onExpire();
      return;
    }
    raf = window.requestAnimationFrame(tick);
  };
  raf = window.requestAnimationFrame(tick);
  return {
    el: node,
    stop: () => {
      window.cancelAnimationFrame(raf);
    },
  };
}

/* ── Cooking ──────────────────────────────────────────────────────────────────────────── */

/** Two beats per serving, named for the food. The mechanic is the same for every cuisine. */
const BEATS: Readonly<Record<string, readonly [string, string]>> = {
  burger: ['Grill the patty', 'Build the burger'],
  hotdog: ['Roll the frank', 'Dress the bun'],
  pizza: ['Spread and top', 'Pull it from the oven'],
  taco: ['Sear the filling', 'Fold the shell'],
  burrito: ['Warm the tortilla', 'Roll it tight'],
  sushi: ['Press the rice', 'Slice the roll'],
  noodle: ['Boil the noodles', 'Toss the wok'],
  crepe: ['Pour the batter', 'Flip and fold'],
  fishchips: ['Drop the basket', 'Salt and wrap'],
};
const DEFAULT_BEATS: readonly [string, string] = ['Cook it', 'Plate it'];

/** Clicks to finish one serving. Two beats: it makes a rhythm rather than a single tap. */
const BEATS_PER_SERVING = 2;
/** The most one session will cook. Long enough to matter, short enough not to be a chore. */
export const PREP_SESSION_MAX = 10;

export interface PrepResult {
  /** Mean timing quality across every beat played, in [0,1]. */
  readonly quality: number;
  /** Servings actually finished. Half-cooked ones do not count. */
  readonly servings: number;
}

export interface PrepGame {
  readonly el: HTMLElement;
  readonly done: Promise<PrepResult | null>;
  cancel: () => void;
}

/**
 * Cook a batch, one serving at a time.
 *
 * A single mechanic — stop the sweep in the hot zone — repeated two beats to a serving, for
 * every cuisine. Cooking ten portions is ten portions of work, not one roll of the dice, and
 * you can stop whenever you like and keep what you have made.
 */
export function prepGame(cuisine: string | null, maxServings: number): PrepGame {
  const cap = Math.max(1, Math.min(PREP_SESSION_MAX, Math.floor(maxServings)));
  const beats = (cuisine !== null ? BEATS[cuisine] : undefined) ?? DEFAULT_BEATS;

  const track = el('div', { class: 'gf-mg-track' });
  const zone = el('div', { class: 'gf-mg-zone' });
  const marker = el('div', { class: 'gf-mg-marker' });
  track.append(zone, marker);

  const counter = el('span', { class: 'gf-mg-count' }, `0 / ${String(cap)}`);
  const verb = el('p', { class: 'gf-mg-label' }, beats[0]);
  const dots = el('div', { class: 'gf-mg-pips' });
  const doneBtn = el(
    'button',
    { class: 'gf-mg-done', attrs: { type: 'button' } },
    'DONE',
  );

  const node = el(
    'div',
    { class: 'gf-mg gf-mg--prep' },
    el(
      'div',
      { class: 'gf-mg-head' },
      el('h4', { class: 'gf-mg-title' }, 'COOKING'),
      counter,
    ),
    verb,
    track,
    dots,
    doneBtn,
  );

  for (let i = 0; i < cap; i += 1) dots.appendChild(el('span', { class: 'gf-mg-pip' }));

  const d = deferred<PrepResult>();
  const scores: number[] = [];
  let beat = 0;        // which half of the current serving
  let made = 0;        // servings finished

  function startSweep(): void {
    // The zone tightens a little as the batch goes on, so a long session stays interesting.
    const half = Math.max(0.075, 0.15 - made * 0.006);
    const centre = 0.22 + Math.random() * 0.56;   // off the ends, where the sweep dwells
    zone.style.left = `${String((centre - half) * 100)}%`;
    zone.style.width = `${String(half * 2 * 100)}%`;
    marker.style.animationDuration = `${String(Math.max(850, 1450 - made * 45))}ms`;
    marker.style.animationName = 'none';
    void marker.offsetWidth;                       // force a reflow so the restart takes
    marker.style.animationName = 'gf-mg-sweep';
    marker.dataset['centre'] = String(centre);
    marker.dataset['half'] = String(half);
  }

  function commit(): void {
    if (d.settled) return;

    const trackBox = track.getBoundingClientRect();
    const markerBox = marker.getBoundingClientRect();
    const pos =
      trackBox.width > markerBox.width
        ? (markerBox.left - trackBox.left) / (trackBox.width - markerBox.width)
        : 0.5;

    const centre = Number(marker.dataset['centre'] ?? '0.5');
    const half = Number(marker.dataset['half'] ?? '0.15');
    const miss = Math.abs(pos - centre);
    // Inside the zone is a clean hit; outside it falls off over roughly a quarter of the bar,
    // so a wild miss still leaves something rather than nothing.
    const score = miss <= half ? 1 : Math.max(0, 1 - (miss - half) / 0.25);
    scores.push(score);

    beat += 1;
    if (beat >= BEATS_PER_SERVING) {
      beat = 0;
      const pip = dots.children[made];
      if (pip instanceof HTMLElement) {
        const half2 = scores.slice(-BEATS_PER_SERVING);
        const mean = half2.reduce((a, b) => a + b, 0) / half2.length;
        pip.className = `gf-mg-pip ${mean >= 0.999 ? 'is-hit' : mean > 0.4 ? 'is-near' : 'is-miss'}`;
      }
      made += 1;
      counter.textContent = `${String(made)} / ${String(cap)}`;
      if (made >= cap) {
        finish();
        return;
      }
    }
    verb.textContent = beats[beat] ?? DEFAULT_BEATS[0];
    startSweep();
  }

  function finish(): void {
    if (d.settled) return;
    marker.style.animationPlayState = 'paused';
    cleanup();
    if (made === 0) {
      d.settle({ quality: 1, servings: 0 });
      return;
    }
    const mean = scores.reduce((a, b) => a + b, 0) / scores.length;
    verb.textContent =
      mean >= 0.95 ? 'Perfect batch!' : mean >= 0.6 ? 'Good enough.' : 'Some of that is ruined.';
    window.setTimeout(() => {
      d.settle({ quality: mean, servings: made });
    }, 420);
  }

  const onPointer = (): void => {
    commit();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      finish();
    }
  };

  track.addEventListener('pointerdown', onPointer);
  doneBtn.addEventListener('click', finish);
  window.addEventListener('keydown', onKey);

  function cleanup(): void {
    track.removeEventListener('pointerdown', onPointer);
    doneBtn.removeEventListener('click', finish);
    window.removeEventListener('keydown', onKey);
  }

  startSweep();
  return {
    el: node,
    done: d.promise,
    cancel: (): void => {
      cleanup();
      d.settle(null);
    },
  };
}

/* ── Wiping down ──────────────────────────────────────────────────────────────────────── */

const GRIME_SPOTS = 7;
const GRIME_PASSES = 2;
const CLEAN_SECONDS = 6;

/**
 * Scrub the grime off. Drag over each spot until it lifts, before the timer runs out; quality is
 * the fraction you got to. Pointer-driven rather than click-driven so it feels like wiping.
 */
export function cleanGame(): Game {
  const surface = el('div', { class: 'gf-mg-surface' });
  const label = el('p', { class: 'gf-mg-label' }, 'Drag across the grease — get it all');

  const node = el(
    'div',
    { class: 'gf-mg gf-mg--clean' },
    el('h4', { class: 'gf-mg-title' }, 'WIPING DOWN'),
    label,
    surface,
  );

  interface Spot {
    readonly node: HTMLElement;
    readonly x: number;
    readonly y: number;
    passes: number;
  }
  const spots: Spot[] = [];
  for (let i = 0; i < GRIME_SPOTS; i += 1) {
    // Jittered rows rather than pure random, so no two spots stack and the whole surface is used.
    const col = i % 4;
    const row = Math.floor(i / 4);
    const x = 0.12 + col * 0.25 + (Math.random() - 0.5) * 0.08;
    const y = row === 0 ? 0.3 + Math.random() * 0.12 : 0.66 + Math.random() * 0.12;
    const size = 20 + Math.random() * 14;
    const spot = el('span', { class: 'gf-mg-grime' });
    spot.style.left = `${String(x * 100)}%`;
    spot.style.top = `${String(y * 100)}%`;
    spot.style.width = `${String(size)}px`;
    spot.style.height = `${String(size)}px`;
    surface.appendChild(spot);
    spots.push({ node: spot, x, y, passes: 0 });
  }

  const d = deferred<number>();
  let cleaned = 0;

  const wipe = (e: PointerEvent): void => {
    if (d.settled) return;
    // Buttons pressed, or a touch drag — a hover with no contact should not clean anything.
    if (e.pointerType === 'mouse' && e.buttons === 0) return;
    const box = surface.getBoundingClientRect();
    const px = (e.clientX - box.left) / box.width;
    const py = (e.clientY - box.top) / box.height;

    for (const s of spots) {
      if (s.passes >= GRIME_PASSES) continue;
      const dx = (px - s.x) * box.width;
      const dy = (py - s.y) * box.height;
      if (dx * dx + dy * dy > 30 * 30) continue;
      s.passes += 1;
      s.node.style.opacity = String(1 - s.passes / GRIME_PASSES);
      if (s.passes >= GRIME_PASSES) {
        s.node.classList.add('is-gone');
        cleaned += 1;
        if (cleaned >= GRIME_SPOTS) finish();
      }
    }
  };

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') cancel();
  };

  surface.addEventListener('pointerdown', wipe);
  surface.addEventListener('pointermove', wipe);
  window.addEventListener('keydown', onKey);

  const timer = timerBar(CLEAN_SECONDS, () => {
    finish();
  });
  node.appendChild(timer.el);

  function cleanup(): void {
    timer.stop();
    surface.removeEventListener('pointerdown', wipe);
    surface.removeEventListener('pointermove', wipe);
    window.removeEventListener('keydown', onKey);
  }
  function finish(): void {
    if (d.settled) return;
    cleanup();
    const q = cleaned / GRIME_SPOTS;
    label.textContent =
      q >= 0.999 ? 'Spotless.' : q >= 0.5 ? 'Better than it was.' : 'Barely touched it.';
    window.setTimeout(() => {
      d.settle(q);
    }, 400);
  }
  function cancel(): void {
    cleanup();
    d.settle(null);
  }

  return { el: node, done: d.promise, cancel };
}
